import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { UserAPIModelsERoles } from 'user-ts-ng-client';

import { AuthService } from '@core/auth/auth.service';

/**
 * Restricts access to Contract Advisor (and client portal) to external users only.
 * Internal users (default organization = Guid.Empty) are redirected to Engagements.
 * Does not change behavior for any role when the user is external.
 */
export const externalUserOnlyGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.isInternalUser()) {
    const externalUserRedirect = getExternalUserRedirect(route.queryParamMap, state, router);
    if (externalUserRedirect) {
      return externalUserRedirect;
    }

    return true;
  }

  const selectedOrganizationId = route.queryParamMap.get('organizationId')?.trim();
  const role = authService.getRole();
  const isEligibleSrmRole =
    role === UserAPIModelsERoles.GlobalAdmin ||
    role === UserAPIModelsERoles.SrmContributor ||
    role === UserAPIModelsERoles.SrmManager;

  if (!selectedOrganizationId || !isEligibleSrmRole) {
    void router.navigate(['/projects']);
    return false;
  }

  return true;
};

function getExternalUserRedirect(
  queryParamMap: { keys: string[]; getAll: (name: string) => string[] },
  state: RouterStateSnapshot,
  router: Router,
): UrlTree | null {
  if (!queryParamMap.keys.includes('organizationId')) {
    return null;
  }

  const path = state.url.split('?')[0] || '/advisor';
  const queryParams = new URLSearchParams();

  queryParamMap.keys
    .filter((key) => key !== 'organizationId')
    .forEach((key) => {
      queryParamMap.getAll(key).forEach((value) => queryParams.append(key, value));
    });

  const queryString = queryParams.toString();
  return router.parseUrl(queryString ? `${path}?${queryString}` : path);
}
